import React from 'react';
import { FaUserCheck, FaQuestionCircle, FaCalendarAlt, FaBell, FaRedo, FaPhone } from 'react-icons/fa';
import { PhoneButton, ButtonText } from './HeroSectionStyles';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  ProcessSection,
  ProcessTitle,
  ProcessFlow,
  StepContainer,
  StepIconContainer,
  StepIcon,
  StepContent,
  StepTitle,
  StepDescription,
  DataParticle,
  TechCircle,
  StepCorner,
  DataPoint
} from './ProcessFlowStyles';

// Container for phone button inside flow
const FlowPhoneButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2.5rem auto 1rem;
  width: 100%;
  text-align: center;
  
  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

// Centered button text
const AdjustedButtonText = styled(ButtonText)`
  position: relative;
  left: 0;
  text-align: center;
  margin: 1rem auto 0;
`;

const ProcessFlowSectionUS = ({ onOpenModal }) => {
  // Check if we're on the /ty-us route
  const location = useLocation();
  const isThankYouPage = location.pathname === '/ty-us';
  
  // Process steps data in English
  const processSteps = [
    {
      icon: <FaUserCheck />,
      title: "Qualification",
      description: "Qualifying questions defined by you, filtering qualified leads."
    },
    {
      icon: <FaQuestionCircle />,
      title: "Clarification",
      description: "Space for leads to ask all their questions about the offer."
    },
    {
      icon: <FaCalendarAlt />,
      title: "Scheduling",
      description: "Capture the best day and time for the lead and appointment confirmation."
    },
    {
      icon: <FaBell />,
      title: "Reminders",
      description: "Sending reminders 24h before and on the morning of the appointment."
    },
    {
      icon: <FaRedo />,
      title: "Follow Up",
      description: "Reactivation of leads who didn't schedule via call and SMS."
    }
  ];

  // Function to generate random data particles
  const renderDataParticles = (count, containerIndex) => {
    const particles = [];
    const colors = ['#00FFFF', '#64B6FF', '#0080FF', '#FFFFFF'];
    
    for (let i = 0; i < count; i++) {
      const top = `${Math.random() * 100}%`;
      const left = `${Math.random() * 100}%`;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const duration = `${1 + Math.random() * 2}s`;
      const delay = `${Math.random() * 2}s`;
      
      particles.push(
        <DataParticle 
          key={`particle-${containerIndex}-${i}`}
          top={top}
          left={left}
          color={color}
          duration={duration}
          delay={delay}
        />
      );
    }
    
    return particles;
  };

  // Function to generate decorative tech circles
  const renderTechCircles = () => {
    const circles = [];
    const sizes = ['15px', '20px', '25px', '30px', '40px'];
    const positions = [
      { top: '10%', left: '5%' },
      { top: '20%', left: '80%' },
      { top: '40%', left: '90%' },
      { top: '60%', left: '5%' },
      { top: '80%', left: '85%' },
      { top: '90%', left: '20%' }
    ];
    
    positions.forEach((pos, index) => {
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      const opacity = (0.2 + Math.random() * 0.3).toFixed(2);
      const duration = `${3 + Math.random() * 3}s`;
      const delay = `${3 + Math.random() * 2}s`;
      
      circles.push(
        <TechCircle 
          key={`circle-${index}`}
          top={pos.top}
          left={pos.left}
          size={size}
          opacity={opacity}
          duration={duration}
          delay={delay}
        />
      );
    });
    
    return circles;
  };

  return (
    <ProcessSection>
      <ProcessTitle>SERVICE FLOW</ProcessTitle>
      
      {/* Decorative tech circles */}
      {renderTechCircles()}
      
      <ProcessFlow>
        {processSteps.map((step, index) => (
          <StepContainer key={index} index={index}>
            <StepIconContainer>
              <StepIcon>{step.icon}</StepIcon>
              {/* Step number inside icon */}
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 32, 64, 0.9)',
                border: '1px solid rgba(0, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '0.7rem',
                fontWeight: 'bold',
                color: '#00FFFF',
                zIndex: 3,
                boxShadow: '0 0 8px rgba(0, 255, 255, 0.5)'
              }}>
                {index + 1}
              </div>
              {renderDataParticles(5, index)}
            </StepIconContainer>
            
            <StepContent index={index}>
              {/* Tech corners */}
              <StepCorner className="top-left" />
              <StepCorner className="top-right" />
              <StepCorner className="bottom-left" />
              <StepCorner className="bottom-right" />
              
              {/* Animated data points */}
              <DataPoint className="top-left" index={index} />
              <DataPoint className="top-right" index={index} />
              <DataPoint className="bottom-left" index={index} />
              <DataPoint className="bottom-right" index={index} />
              
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepContent>
          </StepContainer>
        ))}
      </ProcessFlow>
      
      {/* Free Trial button inside service flow - not displayed on thank you page */}
      {!isThankYouPage && onOpenModal && (
        <FlowPhoneButtonContainer>
          <div>
            <PhoneButton onClick={() => onOpenModal(false)}>
              <FaPhone />
            </PhoneButton>
            <AdjustedButtonText>Free Trial</AdjustedButtonText>
          </div>
        </FlowPhoneButtonContainer>
      )}
    </ProcessSection>
  );
};

export default ProcessFlowSectionUS;
